import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Util } from '../../util';

type AppKey = 'eepay';

interface PsgNavigateDetail {
  app: AppKey;
  path: string;
}

interface MfeRouteChangeDetail {
  app: AppKey;
  path: string;
}

const PSG_NAVIGATE_EVENT = 'psg:navigate';
const PSG_MFE_ROUTE_CHANGE_EVENT = 'psg:mfeRouteChange';

@Injectable({ providedIn: 'root' })
export class HostLocationService {
  private readonly appKey: AppKey = 'eepay';

  private syncingFromHost = false;
  private initialized = false;

  // Ready to emit after we've seen the first stable nav
  private initComplete = false;
  private lastEmittedPath = '';

  // Whether PSG is embedded in a parent frame (PaymentServicesGW.aspx)
  private readonly isEmbedded: boolean;

  private routerSub?: Subscription;
  private readonly onHostNavigateBound: (event: Event) => void;
  private readonly onParentMessageBound: ((event: MessageEvent) => void) | null;

  constructor(private readonly router: Router) {
    this.isEmbedded = window.parent !== null && window.parent !== window;
    this.onHostNavigateBound = (event: Event) => this.onHostNavigate(event);

    if (this.isEmbedded) {
      this.onParentMessageBound = (msg: MessageEvent) => this.onParentMessage(msg);
    } else {
      this.onParentMessageBound = null;
    }
  }

  public handleNavigation(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // fresh run each time the element is mounted (service is singleton)
    this.initComplete = false;
    this.lastEmittedPath = '';

    window.addEventListener(PSG_NAVIGATE_EVENT, this.onHostNavigateBound);

    // When embedded, listen for parent's psg:navigate postMessages.
    // The parent sends these on popstate (browser Back/Forward) so we can
    // replay the navigation inside the MFE.
    if (this.onParentMessageBound) {
      window.addEventListener('message', this.onParentMessageBound);
    }

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const fullRouteUrl = e.urlAfterRedirects || e.url;
        const normalized = this.normalizeForHost(fullRouteUrl);

        // First NavigationEnd after mount is always the host-requested route.
        // PSG already pushed a history entry for this navigation, so we must
        // NOT echo it back — otherwise the parent creates a duplicate entry
        // and the user needs an extra Back press to leave the tab.
        if (!this.initComplete) {
          this.initComplete = true;
          this.lastEmittedPath = normalized;
          return;
        }

        if (this.syncingFromHost) {
          return;
        }

        if (!normalized || normalized === this.lastEmittedPath) {
          return;
        }

        this.lastEmittedPath = normalized;

        const detail: MfeRouteChangeDetail = { app: this.appKey, path: normalized };

        window.dispatchEvent(
          new CustomEvent<MfeRouteChangeDetail>(PSG_MFE_ROUTE_CHANGE_EVENT, { detail })
        );
      });
  }

  public destroy(): void {
    this.routerSub?.unsubscribe();
    this.routerSub = undefined;

    window.removeEventListener(PSG_NAVIGATE_EVENT, this.onHostNavigateBound);

    if (this.onParentMessageBound) {
      window.removeEventListener('message', this.onParentMessageBound);
    }

    this.initialized = false;
    this.initComplete = false;
    this.lastEmittedPath = '';
    this.syncingFromHost = false;
  }

  /**
   * Handles psg:navigate postMessages from the parent frame.
   * The parent sends these on popstate (browser Back/Forward) when it has
   * MFE route info in the URL. We replay the navigation inside the MFE.
   */
  private onParentMessage(msg: MessageEvent): void {
    if (msg.source !== window.parent) {
        return;
      }

    const data = msg.data;
    if (!data) {
      return;
    }

    if (data.type !== PSG_NAVIGATE_EVENT) {
        return;
      }
    if (data.app !== this.appKey) {
      return;
    }

    const path = data.path || '/';

    this.lastEmittedPath = this.normalizeForHost(path);
    this.syncingFromHost = true;

    void this.router.navigateByUrl(path, { replaceUrl: true })
      .catch((err) => console.log('[EEPay HostLocationService] Parent nav error:', err))
      .finally(() => {
        this.syncingFromHost = false;
        this.initComplete = true;
        this.lastEmittedPath = this.normalizeForHost(this.router.url);
      });
  }

  private onHostNavigate(event: Event): void {
    const detail = (event as CustomEvent<PsgNavigateDetail>).detail;
    if (!detail || detail.app !== this.appKey) {
      return;
    }

    const path = detail.path || '/';

    this.syncingFromHost = true;

    void this.router.navigateByUrl(path, { replaceUrl: true }).then(
    ).finally(() => {
      this.syncingFromHost = false;
      this.lastEmittedPath = this.normalizeForHost(this.router.url);
    });
  }

  private normalizeForHost(fullRouteUrl: string): string {
    let navigateTo = Util.removeOutletFromUrl(fullRouteUrl);

    if (navigateTo === '/' && fullRouteUrl.indexOf('(') >= 0) {
      navigateTo = fullRouteUrl;
    }

    return navigateTo;
  }
}
